// File: frontend/src/components/ui/SectionCard.js
//
// Reusable surface: a Paper with the app's standard border, radius and padding.
// Optional header (title + subtitle) and toolbar (right-aligned actions). Set
// `noPadding` for edge-to-edge content such as tables. All colours are theme
// tokens — clean white in light mode, raised surface in dark mode.

import React from "react";
import { Box, Paper, Typography, Stack, Divider } from "@mui/material";

export default function SectionCard({
  title,
  subtitle,
  toolbar,          // right-aligned header actions
  children,
  noPadding = false,
  headerDivider = true,
  sx,
  contentSx,
}) {
  const hasHeader = Boolean(title || subtitle || toolbar);

  return (
    <Paper
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        minWidth: 0,
        ...sx,
      }}
    >
      {hasHeader && (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.25,
              px: { xs: 2, sm: 2.5 },
              py: { xs: 1.5, sm: 1.75 },
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              {title && (
                <Typography sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 700, color: "text.primary", lineHeight: 1.3 }}>
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
                  {subtitle}
                </Typography>
              )}
            </Box>
            {toolbar && (
              <Stack direction="row" gap={1} sx={{ flexShrink: 0, flexWrap: "wrap" }}>
                {toolbar}
              </Stack>
            )}
          </Box>
          {headerDivider && <Divider sx={{ borderColor: "divider" }} />}
        </>
      )}

      <Box sx={{ p: noPadding ? 0 : { xs: 2, sm: 2.5 }, minWidth: 0, ...contentSx }}>
        {children}
      </Box>
    </Paper>
  );
}
